import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, BookOpen, Star } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import ProstoHeader from "@/prosto/ProstoHeader";
import ProstoInstallGuide from "@/prosto/ProstoInstallGuide";
import ProstoAccountModal from "@/prosto/ProstoAccountModal";
import AppIcon from "@/components/AppIcon";
import Badge from "@/components/Badge";

function Stat({ value, label }) {
  return (
    <div className="flex-1 text-center">
      <div className="font-display font-bold text-base text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}

export default function ProstoAppDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .app(id)
      .then((d) => {
        setApp(d);
        track("view_app", `Смотрит «${d.name}»`, { app: d.name });
      })
      .catch(() => setApp(false));
  }, [id]);

  if (app === false) {
    return (
      <div className="min-h-screen">
        <ProstoHeader title="Приложение" back />
        <div className="p-6 text-slate-500">Приложение не найдено.</div>
      </div>
    );
  }
  if (!app) return <div className="min-h-screen" />;

  return (
    <div className="pb-20">
      <ProstoHeader title={app.name} back />

      <div className="px-4 pt-5">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex gap-4">
          <AppIcon src={app.icon} alt={app.name} size={92} className="rounded-[22px] border border-slate-200" />
          <div className="min-w-0 flex-1 self-center">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold text-slate-900 leading-tight truncate">{app.name}</h1>
              <Badge text={app.badge} />
            </div>
            <div className="text-sm text-slate-500 truncate">{app.subtitle || app.developer}</div>
            <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
              <Star size={13} className="fill-amber-400 text-amber-400" />
              <span className="text-slate-900 font-semibold">{app.rating}</span>
            </div>
          </div>
        </motion.div>

        <div className="mt-5 flex gap-3">
          <button
            data-testid="prosto-get-account-cta"
            onClick={() => setModal(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-white bg-[#2F6BFF] active:scale-95 transition shadow-lg shadow-blue-500/25"
          >
            <Download size={18} /> Получить аккаунт
          </button>
          <button
            data-testid="prosto-instruction-cta"
            onClick={() => setModal(true)}
            className="shrink-0 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-semibold text-slate-700 bg-slate-100 active:scale-95 transition"
          >
            <BookOpen size={18} /> Инструкция
          </button>
        </div>

        <div className="mt-5 flex items-center rounded-2xl bg-slate-50 border border-slate-200 py-3 divide-x divide-slate-200">
          <Stat value={`${app.rating} ★`} label={`${app.ratings_count}`} />
          <Stat value={app.age || "4+"} label="возраст" />
          <Stat value={app.category || "—"} label="категория" />
          <Stat value={app.size || "—"} label="размер" />
        </div>

        {app.screenshots?.length > 0 && (
          <div className="mt-6 flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 snap-x">
            {app.screenshots.map((s, i) => (
              <img
                key={i}
                src={s}
                alt={`screenshot ${i + 1}`}
                loading="lazy"
                className="snap-start shrink-0 w-[62%] rounded-2xl border border-slate-200 object-cover"
                style={{ aspectRatio: "9/16" }}
              />
            ))}
          </div>
        )}

        <section className="mt-6">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-2">Описание</h3>
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{app.description}</p>
        </section>

        <section id="prosto-guide" className="mt-8 scroll-mt-20">
          <h3 className="font-display text-xl font-bold text-slate-900">Как установить</h3>
          <p className="text-sm text-slate-500 mb-4">Получите общий аккаунт кнопкой выше и следуйте шагам.</p>
          <ProstoInstallGuide />
        </section>
      </div>

      <ProstoAccountModal open={modal} onOpenChange={setModal} appName={app.name} />
    </div>
  );
}
